<template>
	<div :id="uid" :variant="variant" class="user-card">
		<div class="user-card__status">
			<NotificationIcon
				:variant="notificationVariantIcon"
				ref="tooltipStatusRef"
				@mouseenter="tooltipStatusVisible = true"
				@focus="tooltipStatusVisible = true"
				@mouseleave="tooltipStatusVisible = false"
				@blur="tooltipStatusVisible = false"
			/>
			<Tooltip
				:text="userStatus"
				:trackedRefName="'tooltipStatusRef'"
				:display="tooltipStatusVisible"
				:offset="[4, 4]"
				position="top-start"
			/>
		</div>
		<div class="user-card__main">
			<div :id="`${uid}_name`" class="user-card__name">
				{{ user.firstName }} {{ user.lastName }}
			</div>
			<div class="user-card__email" :id="`${uid}_email`">
				{{ user.email }}
			</div>
			<div class="user-card__userType" :id="`${uid}_userType`">
				<TagLabel
					v-if="isConsultant"
					message="Consultant User"
					:state="'secondary-3'"
				/>
				<TagLabel
					v-if="isInternalUser"
					message="Internal User"
					:state="'secondary-2'"
				/>
				<TagLabel
					v-if="isBothUser"
					message="Consultant & Internal User"
					:state="'secondary-1'"
				/>
			</div>
		</div>
		<div class="user-card__details">
			<div
				class="user-card__detail user-card__detail-roles"
				ref="userCardRolesRef"
				v-if="hasRoles"
			>
				<span class="user-card__label">{{ showRolesLabel }}</span>
				<span class="user-card__data">{{ showRoles }}</span>
			</div>
			<div class="user-card__detail">
				<span class="user-card__label">
					Invalid Attempts -
				</span>
				<span class="user-card__data">
					{{ user.accessFailedCount }}
				</span>
			</div>
		</div>
		<ActionsSet
			class="user-card__actions"
			:id="`${uid}_action`"
			:variant="variant"
			:options="actions"
		/>
	</div>
</template>

<script>
import ActionsSet from '@/site-components/actions-set/actions-set';
import NotificationIcon from '@/components/notifications/notification-icon/notification-icon';
import Tooltip from '@/components/shells/tooltip/tooltip';
import TagLabel from '@/components/visual/tag-label';
import {
	SUPPORT_EXTERNAL,
	SUPPORT_PORTAL
} from '@/sections/users/shared/users-const.js';
import { mapActions } from 'vuex';
import _ from 'lodash';

export default {
	name: 'UserCard',
	components: {
		ActionsSet,
		NotificationIcon,
		Tooltip,
		TagLabel
	},
	data() {
		return {
			tooltipStatusVisible: false,
			tooltipTitleVisible: false,
			tooltipCodeVisible: false
		};
	},
	props: {
		user: {
			type: Object,
			default: () => {}
		}
	},
	computed: {
		settingsUserDisabled() {
			return this.validatePermission('users.update');
		},
		showRolesLabel() {
			if (this.hasRoles) {
				return this.user.roles.length > 1 ? 'Roles - ' : 'Role - ';
			}
			return ' ';
		},
		userStatus() {
			const statusMap = {
				isCorrupted: 'Corrupted',
				inactive: 'Inactive',
				locked: 'Locked'
			};

			if (this.user.isCorrupted) {
				return statusMap.isCorrupted;
			}
			if (this.user.locked) {
				return statusMap.locked;
			}

			if (!this.user.active) {
				return statusMap.inactive;
			}

			return 'Active';
		},
		notificationVariantIcon() {
			if (this.user.isCorrupted) {
				return 'corrupted';
			}
			if (this.user.locked) {
				return 'not-allowed';
			}
			if (!this.user.active) {
				return 'inactive';
			}

			return 'positive';
		},
		actions() {
			return [
				{
					id: 1,
					action: this.updateUserLock,
					disabled: !this.settingsUserDisabled,
					iconClass: 'icon icon-unlock',
					label: 'Unlock',
					title: 'Select to unlock user',
					type: 'anchor',
					visible: this.user.locked
				},
				{
					id: 2,
					action: {
						path: this.$route.path,
						query: {
							userId: this.user.userId
						}
					},
					disabled: !this.settingsUserDisabled,
					iconClass: 'icon icon-manage',
					label: 'Settings',
					title: !this.settingsUserDisabled
						? " You don't have permission to perform this action."
						: 'Select to go to user Setting.',
					type: 'router-link',
					visible: true
				},
				{
					id: 3,
					action: {
						path: `/users/${this.user.userId}/history`,
						params: {
							changedEntityId: this.user.userId
						}
					},
					disabled: false,
					iconClass: 'icon icon-history',
					label: 'History',
					title: 'Select to go to user history.',
					type: 'route-link',
					visible: true
				}
			];
		},
		isConsultant() {
			return (
				this.user.clientIds?.length === 1 &&
				this.isClientIdPresent(SUPPORT_EXTERNAL)
			);
		},
		isInternalUser() {
			return (
				this.user.clientIds?.length === 1 &&
				this.isClientIdPresent(SUPPORT_PORTAL)
			);
		},
		isBothUser() {
			return (
				this.user.clientIds?.length === 2 &&
				this.isClientIdPresent(SUPPORT_PORTAL) &&
				this.isClientIdPresent(SUPPORT_EXTERNAL)
			);
		},
		showRoles() {
			if (this.hasRoles) {
				const fullRolesDescription = this.user.roles
					.map(r => r.role)
					.join(', ');
				return fullRolesDescription;
			}
			return '';
		},
		hasRoles() {
			return !_.isEmpty(this.user.roles);
		}
	},
	methods: {
		...mapActions('users', {
			updateUserLockAction: 'updateUserLock'
		}),
		async updateUserLock() {
			let user = { ...this.user };
			user.locked = false;
			await this.updateUserLockAction({
				userId: user.userId,
				globalEmployeeId: user.globalEmployeeId
			}).then(userUnlocked => {
				if (userUnlocked) {
					this.$store.dispatch('users/reloadUsers', true);
				}
			});
		},
		isClientIdPresent(clientId) {
			return this.user.clientIds.includes(clientId);
		}
	}
};
</script>

<style lang="scss">
/**
    * User Card
		* Can use SCSS variables = TRUE
		* [Table of Contents]
    *
    * BASE
 */

/*------------------------------------*\
  #BASE
\*------------------------------------*/
@import '@/scss/5-objects/_cards.scss';
@import '@/scss/5-objects/_form.scss';

.user-card {
	@include card;
	display: grid;
	grid-template-rows: auto;
	column-gap: $gutter-lg;
	align-items: start;
	font-size: $default-font-size - 2;
	grid-template-columns: auto 2fr 250px auto;

	@include mq($from: lg) {
		grid-template-columns: auto 2fr 145px auto;
	}

	&__status {
		position: relative;
		@include vertical-horizontal-center-with-flex;
		z-index: 1;
		cursor: default;
	}

	&__main,
	&__details {
		min-width: 100%;

		> * {
			margin-bottom: $gutter-sm;
		}
	}

	&__name {
		@include heading('md');
		text-transform: capitalize;
	}

	&__label {
		color: $neutral-2;
	}

	&__detail-roles {
		display: -webkit-box;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 3;
		overflow: hidden;
		overflow-wrap: break-word;
	}

	&__actions {
		align-self: center;
	}

	&__name,
	&__email {
		@include ellipsis;
	}
}
</style>
